import {setContext, setTags, setUser} from '@sentry/nextjs';
import {CommonData} from '@udemy-v2/common-data';

/**
 * Matches transaction names generated by the catch-all [lang]/[...notFound] route.
 * Next.js and Sentry can render these as different patterns depending on context
 * (e.g. "GET /[lang]/[...notFound]", "/[lang]/:notFound*", "lang/notFound").
 */
const NOT_FOUND_TRANSACTION_RE =
    /(\[lang\]|:lang)\/((\.\.\.\s*notFound|\[\.\.\.notFound\])|(:notFound\*)|notFound(\/|$))/;

/**
 * Returns true if the given transaction name corresponds to the catch-all notFound route.
 * Used to rewrite generic 404 transaction names to actual request paths in Sentry.
 */
export function isNotFoundTransaction(name: string | undefined): boolean {
    return typeof name === 'string' && NOT_FOUND_TRANSACTION_RE.test(name);
}

const resolveSentryDsn = () => {
    const SENTRY_DSN = process.env.SENTRY_DSN as string;
    const SENTRY_DSN_CHINA = process.env.SENTRY_DSN_CHINA as string;

    // If this is server-side, simply return normal Sentry DSN
    if (typeof window === 'undefined') {
        return SENTRY_DSN;
    }

    return window.location.hostname.endsWith('udemy.cn') ? SENTRY_DSN_CHINA : SENTRY_DSN;
};

export interface SentryOptions {
    autoSessionTracking: boolean;
    debug: boolean;
    dsn: string;
    enableLogs: boolean;
    environment: string;
    release: string;
    sampleRate: number;
}

export const getSentryOptions = (overrides: Partial<SentryOptions>) => {
    const DEPLOY_ENV = process.env.DEPLOY_ENV as string;
    const VERSION = process.env.VERSION as string;

    const defaults = {
        dsn: resolveSentryDsn(),
        initialScope: {
            tags: {app_name: process.env.APP_NAME},
        },
        environment: DEPLOY_ENV === 'prod' ? 'PROD' : 'DEV',
        sampleRate: DEPLOY_ENV === 'prod' ? 0.05 : 1,
        autoSessionTracking: false,
        release: VERSION,
        // Enable logs to be sent to Sentry
        enableLogs: false,
        // Setting this option to true will print useful information to the console while you're setting up Sentry.
        debug: false,
    };
    return {
        ...defaults,
        ...overrides,
    };
};

export const setSentryContext = (commonData: CommonData) => {
    setTags({
        app_name: commonData.app.name,
        page_key: commonData.page.pageKey,
        deploy_env: commonData.app.deployEnv,
        version: commonData.app.version,
    });

    if (commonData.user) {
        setUser({
            id: commonData.user?.id,
            email: commonData.user?.email,
            name: commonData.user?.name,
        });
    }

    setContext('commonData', commonData as unknown as Record<string, unknown>);
};
